import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { task, timeout } from 'ember-concurrency';
import { isRegisteredDirty, isRegisteredPristine, discardRegistered } from '../lib/pristine-registry.js';
import { removeLayer, isNuovaDocument, pushLayer, isTopLayer, hasOpenLayerInside } from '../lib/nv-layer-stack.js';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("{{this.updateModal}}\n\n{{!-- Focus: no nv-dialog-focus here (overlays.md §2). A record popup is a form whose fields commit on focusout\n    (invoice lines: updateInvoice): a programmatic frame focus / return that blurs a field mid-edit broke the\n    invoice line flow (bisected 2026-10-21). role / aria-modal / aria-labelledby stay. --}}\n\n{{! `...attributes` on the container, matching BbModalModern. Without it every\n    attribute a consumer put on `<BbModal>` — including a `data-test-*` hook —\n    reached no element at all, so a modal\'s callbacks could not be driven by any\n    test and the hook read in review exactly like a wired one. 57 templates\n    invoke this component. }}\n<div\n  class=\'modal-container\n    {{this.showClass}}\n    {{if @tinyModal \"tiny-modal\"}}\n    {{if @warningModal \"warning-modal\"}}\'\n  style=\'{{css-style \"z-index\" this.zIndexStyle}}\'\n  ...attributes\n>\n  {{#if @showModal}}\n    <div class=\'modal-background\'>\n      <div\n        class=\'modal\'\n        role=\'dialog\'\n        aria-modal=\'true\'\n        aria-labelledby=\'beebee-modal-title-{{this.elementId}}\'\n        {{did-insert this.layerOpened}}\n        {{will-destroy this.layerClosed}}\n      >\n        <div class=\'modal-bar modal-header\' style=\'{{css-style \"z-index\" this.zIndexStyle}}\'>\n          <h4 id=\'beebee-modal-title-{{this.elementId}}\'>\n            {{@title}}\n          </h4>\n          {{#if this.showCloseButton}}\n            <button\n              class=\'close-button hdr-btn\'\n              type=\'button\'\n              aria-label=\'{{t \"close\"}}\'\n              disabled={{this.isBusy}}\n              data-test-close-modal\n              {{on \'click\' this.closeModal}}\n              {{did-insert this.registerCloseButton}}\n              {{on-key \'Escape\' this.escapeKey}}\n            >\n              <FaIcon @icon=\'times\' aria-hidden=\'true\' />\n              <EmberTooltip @popperContainer=\'body\'>\n                {{t \'close\'}}\n              </EmberTooltip>\n            </button>\n          {{/if}}\n        </div>\n        {{#if this.showAlert}}\n          <div class=\'force-close-modal\'>\n            <h5>\n              {{t \'Unsaved data\'}}\n            </h5>\n            <div class=\'grid-x\'>\n              <div class=\'cell small-12 medium-auto\'>\n                <button\n                  class=\'button expanded alert\'\n                  type=\'button\'\n                  disabled={{this.isBusy}}\n                  data-test-force-close-modal\n                  {{on \'click\' this.forceCloseModal}}\n                  {{on-key \'Y\'}}\n                >\n                  {{t \'close without saving\'}}\n                </button>\n              </div>\n              <div class=\'cell small-12 medium-auto\'>\n                <span class=\'button expanded\' data-test-set-show-alert {{on \'click\' (fn (mut this.showAlert) null)}}>\n                  {{t \'stay on this page\'}}\n                </span>\n              </div>\n            </div>\n          </div>\n        {{/if}}\n        {{#if (has-block \'body\')}}\n          {{! Named-block mode: component owns body/footer wrappers }}\n          <div class=\'content-bar modal-body {{if (has-block \"footer\") \"content-footer\"}}\'>\n            {{yield to=\'body\'}}\n          </div>\n          {{#if (has-block \'footer\')}}\n            <div class=\'modal-bar modal-footer\' style=\'{{css-style \"z-index\" this.zIndexStyle}}\'>\n              {{yield to=\'footer\'}}\n            </div>\n          {{/if}}\n        {{else if @fullContent}}\n          <div class=\'content-bar\'>\n            <div class=\'grid grid-padding-y\'>\n              <div class=\'cell auto\'>\n                {{yield}}\n              </div>\n            </div>\n          </div>\n        {{else}}\n          {{! Legacy mode: consumer ships .content-bar + .modal-footer inside default block }}\n          {{yield}}\n        {{/if}}\n      </div>\n    </div>\n  {{/if}}\n</div>");

var _dec, _dec2, _class, _descriptor, _descriptor2;
const ANIMATION_DELAY_SHORT = 50;
const ANIMATION_DELAY_MEDIUM = 150;

/**
 * CSS class constants
 * @constant {string}
 */
const CLASS_IN = 'in';
const CLASS_OUT = 'out';
const CLASS_MODAL_OPEN = 'modal-open';

/**
 * Default z-index value for the modal
 * @constant {string}
 */
const DEFAULT_Z_INDEX = '10';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      component — legacy modal dialog (BbModal) with open/close animation tasks,
 *              dirty-state guard, and busy-lock
 *   extends:   Component (@glimmer/component)
 *   template:  bb-modal.hbs
 *   args:      @showModal, @arrayModal, @closeModal, @item, @zIndex, @showCloseButton,
 *              @tinyModal, @warningModal, @title, @fullContent, @busy
 *   uses:      ember-concurrency (task, timeout), lib/pristine-registry (a form's
 *              registered «nothing changed» answer overrides isNew-dirty on close,
 *              NEW records only; a registered «dirty» answer adds edits held on other
 *              records of an EXISTING one, and its discard runs on «close without saving»),
 *              lib/nv-layer-stack (nuova: Escape closes the topmost modal only)
 *   siblings:  bb-modal-modern.js/.hbs (newer named-block variant, same busy-lock contract)
 *   tests:     tests/integration/components/bb-modal-test.js, tests/unit/components/bb-modal-test.js
 *   gotchas:   `@busy` locks closeModal/forceCloseModal while truthy — see the `isBusy` getter
 *              doc below: closing mid-task unmounts the modal body and ember-concurrency then
 *              auto-cancels the still-running task at its next yield, silently dropping the
 *              rest of a save. willDestroy always strips the `modal-open` body class even if
 *              the parent tears the component down via {{#if}} before closeTask can run.
 *              Escape = ember-keyboard `{{on-key 'Escape'}}` on the ✕: a button is not an
 *              «only when focused» element for ember-keyboard, so it fires from the document
 *              listener wherever the focus is (frame, field) — no own keydown, no double fire.
 *
 * BeebeeModal Component
 *
 * A modal dialog component with animation support, scroll preservation,
 * and dirty state detection for unsaved changes.
 *
 * @class BbModalComponent
 * @extends Component
 *
 * @arg {boolean} showModal - Controls modal visibility
 * @arg {Array} [arrayModal] - Alternative array-based visibility control
 * @arg {Function} closeModal - Callback invoked when modal closes
 * @arg {Object} [item] - Data model to check for dirty state
 * @arg {string} [zIndex='10'] - Custom z-index for the modal
 * @arg {boolean} [showCloseButton=true] - Whether to show the close button
 */
let BbModalComponent = (_dec = task({
  restartable: true
}), _dec2 = task({
  restartable: true
}), _class = class BbModalComponent extends Component {
  constructor(...args) {
    super(...args);
    /**
     * Stable per-instance id used for the dialog's aria-labelledby target.
     * @type {string}
     */
    this.elementId = guidFor(this);
    /**
     * Whether to show the unsaved changes alert
     * @type {boolean}
     */
    _initializerDefineProperty(this, "showAlert", _descriptor, this);
    /**
     * Current visibility class for the modal ('in' or 'out')
     * @type {string}
     */
    _initializerDefineProperty(this, "showClass", _descriptor2, this);
    /**
     * Stored scroll position to restore after modal closes
     * @type {number}
     */
    this.bodyScrollTop = 0;
    // the modal is open and owns the page scroll it saved (set by upTask, cleared by closeTask)
    this._holdsPageScroll = false;
    // Header ✕ (did-insert): Escape clicks it, exactly as the old implicit `{{on-key 'Escape'}}` did.
    this.closeButton = null;
  }
  /**
   * Returns the z-index style value
   * @returns {string}
   */
  get zIndexStyle() {
    return this.args.zIndex ?? DEFAULT_Z_INDEX;
  }

  /**
   * Whether to display the close button
   * @returns {boolean}
   */
  get showCloseButton() {
    return this.args.showCloseButton ?? true;
  }

  /**
   * When truthy, the modal refuses to close: the header ✕ and the Escape key
   * (both routed through `closeModal`) become no-ops. Pass
   * `@busy={{this.someSaveTask.isRunning}}` so the modal — and the children it
   * yields — stay mounted for the WHOLE task. Closing mid-task unmounts the
   * body, and ember-concurrency then auto-cancels the still-running task at its
   * next yield, silently dropping the rest of the save. The save button keeps
   * its own loading/disabled state; this keeps the container alive around it.
   * @returns {boolean}
   */
  get isBusy() {
    return this.args.busy ?? false;
  }

  /**
   * Checks if the modal is currently visible
   * @returns {boolean}
   */
  get isVisible() {
    return this.showClass === CLASS_IN;
  }

  /**
   * Checks if modal should be shown based on showModal or arrayModal args
   * @returns {boolean}
   * @private
   */
  get _shouldShowModal() {
    const {
      showModal,
      arrayModal
    } = this.args;
    return showModal || Array.isArray(arrayModal) && arrayModal.length > 0;
  }

  /**
   * Reactive getter that triggers modal open/close based on showModal arg.
   * Note: This getter has side effects by design for template reactivity.
   * @returns {null}
   */
  get updateModal() {
    if (this.args.showModal) {
      this.upTask.perform();
    } else if (this.isVisible) {
      this.closeTask.perform();
    }
    return null;
  }

  /**
   * Safely adds a class to the document body
   * @param {string} className - The class to add
   * @private
   */
  _addBodyClass(className) {
    document.body?.classList.add(className);
  }

  /**
   * Safely removes a class from the document body
   * @param {string} className - The class to remove
   * @private
   */
  _removeBodyClass(className) {
    document.body?.classList.remove(className);
  }

  /**
   * Safely invokes the closeModal callback if provided
   * @private
   */
  _invokeCloseModal() {
    if (typeof this.args.closeModal === 'function') {
      this.args.closeModal();
    }
  }

  /**
   * Gets the current scroll position
   * @returns {number}
   * @private
   */
  _getScrollTop() {
    return window.pageYOffset || document.documentElement?.scrollTop || 0;
  }

  /**
   * Scrolls the window to a specific position
   * @param {number} top - The scroll position
   * @private
   */
  _scrollTo(top) {
    window.scrollTo(0, top);
  }

  /**
   * Task to open the modal with animation
   * @task
   */
  *upTask() {
    yield timeout(ANIMATION_DELAY_SHORT);
    if (this.isDestroying || this.isDestroyed) return;
    if (!this._shouldShowModal) {
      this.closeTask.perform();
      return;
    }
    this.bodyScrollTop = this._getScrollTop();
    this._holdsPageScroll = true;
    this._addBodyClass(CLASS_MODAL_OPEN);
    this._scrollTo(this.bodyScrollTop);
    this.showClass = CLASS_IN;
    this.showAlert = false;
  }

  /**
   * Task to close the modal with animation
   * @task
   */
  *closeTask() {
    yield timeout(ANIMATION_DELAY_SHORT);
    if (this.isDestroying || this.isDestroyed) return;
    this._removeBodyClass(CLASS_MODAL_OPEN);
    this._scrollTo(this.bodyScrollTop);
    this._holdsPageScroll = false;
    this.showClass = CLASS_OUT;
    yield timeout(ANIMATION_DELAY_MEDIUM);
    if (this.isDestroying || this.isDestroyed) return;
    this._invokeCloseModal();
  }

  /**
   * Ensures modal-open class is always removed when component is destroyed,
   * even if the parent tears it down via {{#if}} before closeTask can run.
   */
  willDestroy() {
    super.willDestroy(...arguments);
    removeLayer(this);
    this._removeBodyClass(CLASS_MODAL_OPEN);
    // «UI nuova»: only a modal that was OPEN gives the page its scroll back. A never-opened modal of the page being
    // left scrolled the window to 0 on every navigation and undid lib/nv-scroll-restore (MOBILE-NATIVE-PLAN W1.5).
    if (this._holdsPageScroll || !isNuovaDocument()) {
      this._scrollTo(this.bodyScrollTop);
    }
    // Cancella timeout di animazione per evitare update su component distrutto
    this.upTask?.cancelAll?.();
    this.closeTask?.cancelAll?.();
  }
  registerCloseButton(element) {
    this.closeButton = element;
  }

  // Open-order stack (lib/nv-layer-stack): the dialog is on screen while it is in the DOM. Bookkeeping only —
  // read in «UI nuova» alone.
  layerOpened(element) {
    this._layerElement = element;
    pushLayer(this, element, () => this.escapeKey()); // Back = Escape (✕ click: busy + dirty alert)
  }
  layerClosed() {
    this._layerElement = null;
    removeLayer(this);
  }

  /**
   * Escape. Classic: click the ✕ (the old `{{on-key 'Escape'}}` behaviour, byte-identical: a disabled —
   * busy — ✕ ignores the click). «UI nuova» (M11 W1, P5): only the TOPMOST layer closes — the last OPENED
   * modal / sheet (open-order stack, not DOM position: an inline modal opened from a BbModalModern sits
   * earlier in the document but is on top).
   */
  escapeKey() {
    const button = this.closeButton;
    if (!button) return;
    if (isNuovaDocument() && !isTopLayer(this)) {
      return;
    }
    // nuova (M11 live review 5): an open pane / combobox inside the modal takes Escape first
    if (isNuovaDocument() && hasOpenLayerInside(this._layerElement)) {
      return;
    }
    button.click();
  }

  /**
   * Action to close the modal, checking for unsaved changes first
   */
  closeModal() {
    document.activeElement?.blur();
    if (!this.args.showModal) {
      return;
    }

    // A task launched from inside the modal is still running (see `isBusy`).
    // Both the ✕ click and the Escape key funnel through here, so this guard
    // keeps the modal — and its children's still-running tasks — mounted until
    // the task clears the busy flag.
    if (this.isBusy) {
      return;
    }

    // An EXISTING record whose edits live on other records (a clinical sheet's field rows) is itself clean:
    // a form that registered a dirty check (lib/pristine-registry) reports those edits so the alert still asks.
    const hasDirtyAttributes = (this.args.item?.hasDirtyAttributes ?? false) || !this.args.item?.isNew && isRegisteredDirty(this.args.item);

    // A NEW record is always hasDirtyAttributes. For a NEW record only, a form that registered a pristine
    // check (lib/pristine-registry, «UI nuova» invoice editor) can answer «nothing changed since it opened».
    // An EXISTING record always takes the classic path: the alert, whose «close without saving» runs
    // rollbackAttributes + safeReload — skipping it would leave open-time recomputed figures dirty in the
    // store for the next save (M11 W3 review, bug 1).
    const pristineNew = Boolean(this.args.item?.isNew) && isRegisteredPristine(this.args.item);
    if (hasDirtyAttributes && !pristineNew) {
      this.showAlert = true;
    } else {
      this.closeTask.perform();
    }
  }

  /**
   * Action to force close the modal, rolling back any unsaved changes
   */
  forceCloseModal() {
    // The unsaved-changes alert can still be on screen when a task starts. Its
    // "close without saving" button rolls the record back and closes — which
    // would unmount a running task's host. Honour `isBusy` here too.
    if (this.isBusy) {
      return;
    }
    const {
      item
    } = this.args;
    if (this.showAlert && item?.id) {
      item.rollbackAttributes?.();
      discardRegistered(item);
      item.safeReload?.();
    }
    this.closeTask.perform();
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "showAlert", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "showClass", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return CLASS_OUT;
  }
}), _applyDecoratedDescriptor(_class.prototype, "upTask", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "upTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "closeTask", [_dec2], Object.getOwnPropertyDescriptor(_class.prototype, "closeTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "registerCloseButton", [action], Object.getOwnPropertyDescriptor(_class.prototype, "registerCloseButton"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "layerOpened", [action], Object.getOwnPropertyDescriptor(_class.prototype, "layerOpened"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "layerClosed", [action], Object.getOwnPropertyDescriptor(_class.prototype, "layerClosed"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "escapeKey", [action], Object.getOwnPropertyDescriptor(_class.prototype, "escapeKey"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "closeModal", [action], Object.getOwnPropertyDescriptor(_class.prototype, "closeModal"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "forceCloseModal", [action], Object.getOwnPropertyDescriptor(_class.prototype, "forceCloseModal"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, BbModalComponent);

export { BbModalComponent as default };
//# sourceMappingURL=bb-modal.js.map
